/**
 * Print-area geometry helpers.
 *
 * Design items are positioned relative to the printable area (the dashed box),
 * not the full stage. These helpers resolve the printable area to stage pixels
 * so bounds checks (preflight) and rendering share one source of truth.
 */

export interface PrintAreaRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Center-cover source dimensions over a target box while preserving aspect ratio. */
export function coverBox(
  sourceWidth: number,
  sourceHeight: number,
  targetWidth: number,
  targetHeight: number,
): PrintAreaRect {
  const sw = Math.max(1, Number(sourceWidth) || 1);
  const sh = Math.max(1, Number(sourceHeight) || 1);
  const tw = Math.max(1, Number(targetWidth) || 1);
  const th = Math.max(1, Number(targetHeight) || 1);
  const scale = Math.max(tw / sw, th / sh);
  const width = sw * scale;
  const height = sh * scale;
  return { left: (tw - width) / 2, top: (th - height) / 2, width, height };
}

/** Built-in shape ids, or any string id registered by an addon. */
export type PrintAreaShapeId = 'rect' | 'rounded' | 'circle' | (string & {});

/**
 * Infer the print-area shape from a parsed area object for rows saved before
 * the `shape` field existed. `radius` is always kept in sync by the admin UI,
 * so this rule covers every legacy row without a DB migration.
 */
export function inferPrintAreaShape(parsedArea: Record<string, unknown> | null): PrintAreaShapeId {
  if (!parsedArea) return 'rect';
  if (typeof parsedArea.shape === 'string' && parsedArea.shape) return parsedArea.shape;
  const radius = String(parsedArea.radius ?? '').trim();
  if (radius === '50%') return 'circle';
  if (radius && radius !== '0' && radius !== '0px' && parseFloat(radius) > 0) return 'rounded';
  return 'rect';
}

/** Extra params for the resolved shape (e.g. `{ radiusPx }` for `rounded`). */
export function resolvePrintAreaShapeParams(parsedArea: Record<string, unknown> | null): Record<string, unknown> {
  if (!parsedArea) return {};
  const radius = String(parsedArea.radius ?? '').trim();
  if (radius && radius !== '50%') {
    const radiusPx = parseFloat(radius) || 0;
    if (radiusPx > 0) return { radiusPx };
  }
  return {};
}

/** Safely parse a JSON string that may use single quotes instead of double quotes. */
export function safeParseAreaJson(str: string, fallback: unknown = null): unknown {
  try {
    return JSON.parse(str.replace(/'/g, '"'));
  } catch {
    return fallback;
  }
}

/**
 * Resolve a CSS length (number, `NNpx`, or `NN%`) to pixels.
 * Percentages are relative to `percentRef` (stage width for left/width, height for top/height).
 */
export function parseCssLenToPx(value: unknown, percentRef: number): number {
  if (value == null || value === '') return 0;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const s = String(value).trim().toLowerCase();
  if (!s || s === 'auto') return 0;
  if (s.endsWith('%')) {
    const n = parseFloat(s);
    return Number.isFinite(n) ? (n / 100) * percentRef : 0;
  }
  if (s.endsWith('px')) {
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : 0;
  }
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Resolve the printable area to pixels on the stage (boxWidth × boxHeight).
 * Matches CSS absolute positioning: left/width % use stage width; top/height % use stage height.
 */
export function resolvePrintAreaPx(
  parsedArea: Record<string, unknown> | null,
  stageW: number,
  stageH: number,
): PrintAreaRect | null {
  if (!parsedArea) return null;
  const left = parseCssLenToPx(parsedArea.left, stageW);
  const top = parseCssLenToPx(parsedArea.top, stageH);
  const width = parseCssLenToPx(parsedArea.width, stageW);
  const height = parseCssLenToPx(parsedArea.height, stageH);
  if (width <= 0 || height <= 0) return null;
  return { left, top, width, height };
}

/**
 * Resolve printable-area pixel sizes for every view from the product's area map.
 * Views with no parsable area are omitted (callers fall back to stage size).
 */
export function resolveViewAreaSizes(
  areas: Record<string, unknown> | null | undefined,
  stageW: number,
  stageH: number,
): Record<string, { width: number; height: number }> {
  const out: Record<string, { width: number; height: number }> = {};
  if (!areas) return out;
  for (const [view, raw] of Object.entries(areas)) {
    const parsed = typeof raw === 'string' ? safeParseAreaJson(raw) : raw;
    const rect = resolvePrintAreaPx(parsed as Record<string, unknown> | null, stageW, stageH);
    if (rect) out[view] = { width: rect.width, height: rect.height };
  }
  return out;
}

/** Cover the active view's printable region; malformed legacy area data falls back to stage size. */
export function coverActivePrintArea(
  sourceWidth: number,
  sourceHeight: number,
  activeView: string,
  areas: Record<string, unknown> | null | undefined,
  stageWidth: number,
  stageHeight: number,
): PrintAreaRect {
  const fallback = {
    width: Math.max(1, Number(stageWidth) || 1),
    height: Math.max(1, Number(stageHeight) || 1),
  };
  const target = resolveViewAreaSizes(areas, fallback.width, fallback.height)[activeView] ?? fallback;
  return coverBox(sourceWidth, sourceHeight, target.width, target.height);
}

/** Physical print sizes stored in product design params (`width`/`height` in cm). */
export function resolveViewPhysicalSizesCm(
  params: Record<string, unknown> | null | undefined,
): Record<string, { width: number; height: number }> {
  const out: Record<string, { width: number; height: number }> = {};
  if (!params) return out;
  for (const [view, raw] of Object.entries(params)) {
    const parsed = typeof raw === 'string' ? safeParseAreaJson(raw) : raw;
    if (!parsed || typeof parsed !== 'object') continue;
    const width = Number((parsed as Record<string, unknown>).width);
    const height = Number((parsed as Record<string, unknown>).height);
    if (Number.isFinite(width) && width > 0 && Number.isFinite(height) && height > 0) {
      out[view] = { width, height };
    }
  }
  return out;
}
